/**
 * Domain tabs backed by the per-domain reads: documents, EPP commands, contacts, nameservers,
 * DNSSEC, orders, billing, invoices and transfers.
 */

import { ArrowRightLeft, Globe2, KeyRound, Receipt, ScrollText, ShoppingCart, Users, Wallet } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, type DomainDetail } from '../../../api';
import type {
  DomainDsRecord,
  DomainEppCommand,
  DomainEppQuery,
  DomainHold,
  DomainInvoice,
  DomainJournalEntry,
  DomainOrderItem,
  DomainRefund,
  EppObjectType,
  ExpiryNotice,
  RegistrantChangeSummary,
  TransferLockRecord,
  DomainTransfers,
} from '../../../api/areas/core';
import { Badge } from '../../../components/Badge';
import { Callout, DataTable, Field, FilterBar, Pager, Section, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EntityRef } from '../../../components/EntityRef';
import { KeyValue } from '../../../components/KeyValue';
import { CopyText, EmptyState, Loading } from '../../../components/States';
import { formatDate, formatDateTime, formatRelative } from '../../../lib/format';
import { useLabels } from '../../../lib/labels';
import { useApi, type UseApiResult } from '../../../lib/useApi';
import {
  ActorName,
  CustomerRef,
  EppResult,
  Money,
  SubjectRef,
  TxRef,
  useNoticeLabel,
  useVocab,
  WorkflowRef,
} from '../refs';
import { BILLING, COMMAND_STATE, humanize, StatusBadge, TRANSFER } from '../status';
import { SystemReason } from '@/components/SystemReason';

type TabProps = { detail: DomainDetail };
type Lookup = (key: string, options?: Record<string, unknown>) => string;

const ANY = '__any';

/** Loading and error states for a section's data; renders `children` once loaded. */
function Loaded<T>({ state, failed, flush, children }: { state: UseApiResult<T>; failed: string; flush?: boolean; children: (data: T) => ReactNode }) {
  if (state.status === 'loading') {
    return (
      <div className={flush ? 'px-5' : undefined}>
        <Loading rows={3} />
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className={flush ? 'px-5 pb-5' : undefined}>
        <DataError error={state.error} onRetry={state.reload} title={failed} />
      </div>
    );
  }
  return <>{children(state.data)}</>;
}

// ---------------------------------------------------------------------------
// Documents

// ---------------------------------------------------------------------------
// EPP commands

const EPP_STATES = Object.keys(COMMAND_STATE);
const EPP_OBJECTS: EppObjectType[] = ['DOMAIN', 'CONTACT', 'HOST'];
const EPP_LIMIT = 25;

export function EppTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const L = useLabels();
  const d = detail.domain;
  const [filters, setFilters] = useState<DomainEppQuery>({});
  const [trid, setTrid] = useState('');
  const [pages, setPages] = useState<string[]>([]);
  const before = pages[pages.length - 1];
  const epp = useApi(
    (signal) => api.listDomainEpp(d.id, { ...filters, before, limit: EPP_LIMIT }, { signal }),
    [d.id, filters.operation, filters.state, filters.object_type, filters.trid, before],
  );
  const set = (patch: Partial<DomainEppQuery>) => {
    setPages([]);
    setFilters((f) => ({ ...f, ...patch }));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    set({ trid: trid.trim() || undefined });
  };
  const filtered = Boolean(filters.operation || filters.state || filters.object_type || filters.trid);

  const columns: Column<DomainEppCommand>[] = [
    {
      key: 'started',
      header: t('epp.started'),
      className: 'whitespace-nowrap',
      cell: (x) => (
        <div>
          <div>{formatDateTime(x.started_at)}</div>
          {x.latency_ms !== null ? <div className="text-xs text-muted-foreground">{t('epp.latency')}: {x.latency_ms} ms</div> : null}
        </div>
      ),
    },
    {
      key: 'command',
      header: t('epp.command'),
      cell: (x) => (
        <TxRef
          id={x.id}
          operation={x.operation}
          sub={
            <>
              {vocab('object', x.object_type)}
              {x.object_type !== 'DOMAIN' && x.object_key ? <span className="font-mono"> {x.object_key}</span> : null}
              {x.attempt > 1 ? ` · ${t('registry.attempt', { n: x.attempt })}` : null}
            </>
          }
        />
      ),
    },
    { key: 'state', header: t('registry.state'), cell: (x) => <StatusBadge map={COMMAND_STATE} value={x.state} /> },
    {
      key: 'result',
      header: t('epp.result'),
      className: 'min-w-36 whitespace-normal',
      cell: (x) =>
        x.result_code !== null ? (
          <EppResult code={x.result_code} message={x.result_message} />
        ) : x.error_class ? (
          <span className="text-xs">{vocab('errorClass', x.error_class)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'trids',
      header: t('epp.trids'),
      cell: (x) => (
        <div className="flex flex-col gap-0.5">
          <CopyText value={x.cltrid} />
          {x.svtrid ? <CopyText value={x.svtrid} /> : <span className="text-xs text-muted-foreground">{t('registry.noSvtrid')}</span>}
        </div>
      ),
    },
    { key: 'wf', header: t('epp.workflow'), className: 'min-w-32 whitespace-normal', cell: (x) => <WorkflowRef id={x.workflow_id} type={x.workflow_type} sub={null} /> },
  ];

  return (
    <Section flush title={t('epp.title')} description={t('epp.description')}>
      <form onSubmit={submit} className="px-5">
        <FilterBar>
          <Field label={t('epp.operation')} className="w-full sm:w-52">
            <Select value={filters.operation ?? ANY} onValueChange={(v) => set({ operation: v === ANY ? undefined : v })}>
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('epp.anyOperation')}</SelectItem>
                {(epp.status === 'success' ? epp.data.operations : []).map((o) => (
                  <SelectItem key={o} value={o}>
                    {L.eppCommand(o)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('epp.state')} className="w-full sm:w-44">
            <Select value={filters.state ?? ANY} onValueChange={(v) => set({ state: v === ANY ? undefined : v })}>
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('epp.anyState')}</SelectItem>
                {EPP_STATES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {(t as unknown as Lookup)(`status.command.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('epp.object')} className="w-full sm:w-40">
            <Select value={filters.object_type ?? ANY} onValueChange={(v) => set({ object_type: v === ANY ? undefined : (v as EppObjectType) })}>
              <SelectTrigger className="w-full bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>{t('epp.anyObject')}</SelectItem>
                {EPP_OBJECTS.map((o) => (
                  <SelectItem key={o} value={o}>
                    {vocab('object', o)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('epp.trid')} className="w-full sm:w-64">
            <Input value={trid} onChange={(e) => setTrid(e.target.value)} placeholder={t('epp.tridPlaceholder')} className="bg-card font-mono" spellCheck={false} />
          </Field>
          <Button type="submit" variant="outline">
            {t('epp.search')}
          </Button>
        </FilterBar>
      </form>
      <Loaded state={epp} failed={t('epp.loadFailed')} flush>
        {(data) => (
          <>
            <DataTable
              columns={columns}
              rows={data.items}
              rowKey={(x) => x.id}
              empty={
                <EmptyState title={t('epp.empty')} icon={ScrollText}>
                  {filtered ? t('epp.emptyFiltered') : t('epp.emptyAll')}
                </EmptyState>
              }
            />
            {data.items.length > 0 || before ? (
              <Pager
                canPrevious={pages.length > 0}
                canNext={Boolean(data.next_before)}
                onPrevious={() => setPages((p) => p.slice(0, -1))}
                onNext={() => data.next_before && setPages((p) => [...p, data.next_before as string])}
                info={t('epp.shown', { count: data.items.length })}
              />
            ) : null}
          </>
        )}
      </Loaded>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Contacts

// ---------------------------------------------------------------------------
// Nameservers and DNSSEC

export const DNSSEC_ALGORITHMS: Record<number, string> = {
  5: 'RSASHA1',
  7: 'RSASHA1-NSEC3-SHA1',
  8: 'RSASHA256',
  10: 'RSASHA512',
  13: 'ECDSAP256SHA256',
  14: 'ECDSAP384SHA384',
  15: 'ED25519',
  16: 'ED448',
};
export const DIGEST_TYPES: Record<number, string> = { 1: 'SHA-1', 2: 'SHA-256', 4: 'SHA-384' };

export function DnssecTab({ detail, extraActions }: TabProps & { extraActions?: ReactNode }) {
  const { t } = useTranslation('domains');
  const d = detail.domain;
  const del = useApi((signal) => api.getDomainDelegation(d.id, { signal }), [d.id]);
  const columns: Column<DomainDsRecord>[] = [
    { key: 'tag', header: t('delegation.keyTag'), numeric: true, cell: (x) => <span className="font-mono">{x.key_tag}</span> },
    {
      key: 'alg',
      header: t('delegation.algorithm'),
      cell: (x) => (
        <span>
          {DNSSEC_ALGORITHMS[x.algorithm] ?? '—'} <span className="text-xs text-muted-foreground">({x.algorithm})</span>
        </span>
      ),
    },
    {
      key: 'dt',
      header: t('delegation.digestType'),
      cell: (x) => (
        <span>
          {DIGEST_TYPES[x.digest_type] ?? '—'} <span className="text-xs text-muted-foreground">({x.digest_type})</span>
        </span>
      ),
    },
    { key: 'digest', header: t('delegation.digest'), cell: (x) => <CopyText value={x.digest} display={`${x.digest.slice(0, 16)}…`} /> },
    { key: 'updated', header: t('delegation.updated'), className: 'whitespace-nowrap', cell: (x) => formatDateTime(x.updated_at) },
  ];
  return (
    <Section
      flush
      title={t('delegation.dsTitle')}
      description={t('delegation.dsDescription')}
      actions={
        <>
          {del.status === 'success' ? (
            <Badge tone={del.data.ds_records.length ? 'ok' : 'neutral'}>{del.data.ds_records.length ? t('delegation.signed') : t('delegation.unsigned')}</Badge>
          ) : null}
          {extraActions}
        </>
      }
    >
      <Loaded state={del} failed={t('delegation.loadFailed')} flush>
        {(data) => (
          <>
            {data.supports_dnssec === false ? (
              <div className="px-5 pb-4">
                <Callout tone="warn">{t('delegation.notSupported')}</Callout>
              </div>
            ) : null}
            <DataTable
              columns={columns}
              rows={data.ds_records}
              rowKey={(x) => `${x.key_tag}-${x.algorithm}-${x.digest_type}-${x.digest}`}
              empty={
                <EmptyState title={t('delegation.noDs')} icon={KeyRound}>
                  {t('delegation.noDsBody')}
                </EmptyState>
              }
            />
          </>
        )}
      </Loaded>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Orders, billing, invoices

function useCommerce(detail: DomainDetail) {
  const id = detail.domain.id;
  return useApi((signal) => api.getDomainCommerce(id, { signal }), [id]);
}

function orderTone(status: string) {
  if (['COMPLETED', 'FULFILLED', 'CAPTURED'].includes(status)) return 'ok' as const;
  if (['FAILED', 'CANCELLED'].includes(status)) return 'crit' as const;
  if (['REFUNDED', 'RELEASED', 'EXPIRED'].includes(status)) return 'neutral' as const;
  return 'pending' as const;
}

export function OrdersTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const vocab = useVocab();
  const commerce = useCommerce(detail);
  const columns: Column<DomainOrderItem>[] = [
    {
      key: 'order',
      header: t('orders.order'),
      cell: (x) => (
        <EntityRef
          label={L.order({ first_domain: x.order_first_domain, item_count: x.order_item_count, created_at: x.order_created_at })}
          to={`/orders/${encodeURIComponent(x.order_id)}`}
          id={x.order_id}
          sub={
            <>
              {vocab('orderStatus', x.order_status)}
            </>
          }
        />
      ),
    },
    {
      key: 'op',
      header: t('orders.operation'),
      cell: (x) => (
        <div>
          <div>{vocab('operation', x.operation)}</div>
          <div className="text-xs text-muted-foreground">{t('orders.years', { count: x.years })}</div>
        </div>
      ),
    },
    { key: 'amount', header: t('orders.amount'), numeric: true, cell: (x) => <Money amount={x.amount} currency={x.currency} /> },
    {
      key: 'status',
      header: t('orders.status'),
      cell: (x) => (
        <Badge tone={orderTone(x.status)} title={x.status}>
          {vocab('orderStatus', x.status)}
        </Badge>
      ),
    },
    {
      key: 'hold',
      header: t('orders.hold'),
      cell: (x) =>
        x.hold_status ? (
          <Badge tone={orderTone(x.hold_status)} icon={false} title={x.hold_status}>
            {vocab('hold', x.hold_status)}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'invoice',
      header: t('orders.invoice'),
      cell: (x) =>
        x.invoice_id ? (
          <EntityRef label={`${x.invoice_series}-${x.invoice_number}`} to={`/invoices/${encodeURIComponent(x.invoice_id)}`} id={x.invoice_id} />
        ) : (
          <span className="text-xs text-muted-foreground">{t('orders.noInvoice')}</span>
        ),
    },
    { key: 'customer', header: t('orders.customer'), cell: (x) => <CustomerRef id={x.customer_id} name={x.customer_name} /> },
  ];
  return (
    <Section flush title={t('orders.title')} description={t('orders.description')}>
      <Loaded state={commerce} failed={t('orders.loadFailed')} flush>
        {(data) => (
          <DataTable
            columns={columns}
            rows={data.items}
            rowKey={(x) => x.id}
            empty={
              <EmptyState title={t('orders.empty')} icon={ShoppingCart}>
                {t('orders.emptyBody')}
              </EmptyState>
            }
          />
        )}
      </Loaded>
    </Section>
  );
}

function JournalEntries({ entries }: { entries: DomainJournalEntry[] }) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  if (!entries.length) return <EmptyState title={t('billing.noJournal')} icon={ScrollText} />;
  return (
    <ul className="divide-y border-t">
      {entries.map((e) => (
        <li key={e.id} className="grid grid-cols-1 gap-2 px-5 py-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <EntityRef
              label={vocab('entry', e.entry_type)}
              to={`/ledger/journal/${encodeURIComponent(e.id)}`}
              id={e.id}
              // The entry description is a system memo in English; the entry type and the
              // postings below say the same thing, so only the reversal note is shown.
              sub={e.reverses_entry_id ? t('billing.reverses') : undefined}
            />
            <span className="text-xs whitespace-nowrap text-muted-foreground">{formatDateTime(e.posted_at)}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">{t('billing.account')}</th>
                  <th className="py-1 pr-3 text-right font-medium">{t('billing.debit')}</th>
                  <th className="py-1 text-right font-medium">{t('billing.credit')}</th>
                </tr>
              </thead>
              <tbody>
                {e.postings.map((p) => (
                  <tr key={p.id} className="align-top">
                    <td className="py-1 pr-3">
                      <EntityRef
                        label={
                          p.owner_name ??
                          (/[0-9a-f]{8}-[0-9a-f]{4}/i.test(p.account_code) || p.account_code.startsWith('platform:') ? (
                            <span title={p.account_code}>{t('vocab.platformAccount')}</span>
                          ) : (
                            <span className="font-mono text-xs break-all">{p.account_code}</span>
                          ))
                        }
                        to={`/ledger/accounts/${encodeURIComponent(p.account_id)}`}
                        id={p.account_id}
                        sub={vocab('account', p.account_type)}
                      />
                    </td>
                    <td className="py-1 pr-3 text-right">{p.direction === 'DEBIT' ? <Money amount={p.amount} currency={p.currency} /> : null}</td>
                    <td className="py-1 text-right">{p.direction === 'CREDIT' ? <Money amount={p.amount} currency={p.currency} /> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function BillingTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const commerce = useCommerce(detail);
  const holdColumns: Column<DomainHold>[] = [
    { key: 'purpose', header: t('billing.purpose'), cell: (h) => vocab('operation', h.purpose === 'REGISTRATION' ? 'REGISTER' : h.purpose === 'RENEWAL' ? 'RENEW' : h.purpose) },
    { key: 'amount', header: t('billing.amount'), numeric: true, cell: (h) => <Money amount={h.amount} currency={h.currency} /> },
    {
      key: 'status',
      header: t('billing.status'),
      cell: (h) => (
        <Badge tone={orderTone(h.status)} title={h.status}>
          {vocab('hold', h.status)}
        </Badge>
      ),
    },
    { key: 'created', header: t('billing.created'), className: 'whitespace-nowrap', cell: (h) => formatDateTime(h.created_at) },
    { key: 'closed', header: t('billing.closed'), className: 'whitespace-nowrap', cell: (h) => formatDateTime(h.closed_at) },
  ];
  const refundColumns: Column<DomainRefund>[] = [
    { key: 'reason', header: t('billing.reason'), cell: (r) => <SystemReason reason={r.reason} /> },
    { key: 'amount', header: t('billing.amount'), numeric: true, cell: (r) => <Money amount={r.amount} currency={r.currency} /> },
    {
      key: 'status',
      header: t('billing.status'),
      cell: (r) => (
        <Badge tone={r.status === 'COMPLETED' ? 'ok' : r.status === 'FAILED' ? 'crit' : 'pending'} title={r.status}>
          {vocab('refund', r.status)}
        </Badge>
      ),
    },
    { key: 'created', header: t('billing.created'), className: 'whitespace-nowrap', cell: (r) => formatDateTime(r.created_at) },
  ];
  return (
    <Loaded state={commerce} failed={t('billing.loadFailed')}>
      {(data) => (
        <div className="grid grid-cols-1 gap-6">
          <Section title={t('billing.title')} description={t('billing.description')}>
            <KeyValue
              columns={3}
              rows={[
                [t('billing.state'), <StatusBadge key="s" map={BILLING} value={data.billing_state} />],
                [t('billing.customer'), <CustomerRef key="c" id={data.customer_id} name={data.customer_name} />],
                [t('billing.currency'), data.billing_currency],
                [t('billing.paidThrough'), data.customer_expires_at ? `${formatDate(data.customer_expires_at)} (${formatRelative(data.customer_expires_at)})` : '—'],
                [t('billing.registryExpiry'), data.registry_expires_at ? `${formatDate(data.registry_expires_at)} (${formatRelative(data.registry_expires_at)})` : '—'],
                [t('billing.autoRenew'), data.auto_renew ? t('billing.autoRenewOn', { years: data.auto_renew_period }) : t('billing.off')],
              ]}
            />
          </Section>
          <Section flush title={t('billing.holds')} description={t('billing.holdsDescription')}>
            <DataTable columns={holdColumns} rows={data.holds} rowKey={(h) => h.id} empty={<EmptyState title={t('billing.noHolds')} icon={Wallet} />} />
          </Section>
          {data.refunds.length ? (
            <Section flush title={t('billing.refunds')}>
              <DataTable columns={refundColumns} rows={data.refunds} rowKey={(r) => r.id} />
            </Section>
          ) : null}
          <Section flush title={t('billing.journal')} description={t('billing.journalDescription')}>
            <JournalEntries entries={data.journal_entries} />
          </Section>
        </div>
      )}
    </Loaded>
  );
}

export function InvoicesTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const commerce = useCommerce(detail);
  return (
    <Section flush title={t('invoices.title')} description={t('invoices.description')}>
      <Loaded state={commerce} failed={t('invoices.loadFailed')} flush>
        {(data) =>
          data.invoices.length === 0 ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('invoices.empty')} icon={Receipt}>
                {t('invoices.emptyBody')}
              </EmptyState>
            </div>
          ) : (
            <ul className="divide-y border-t">
              {data.invoices.map((inv: DomainInvoice) => (
                <li key={inv.id} className="grid grid-cols-1 gap-3 px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-col gap-1">
                      <EntityRef
                        label={`${inv.series}-${inv.number}`}
                        to={`/invoices/${encodeURIComponent(inv.id)}`}
                        id={inv.id}
                        sub={
                          <>
                            {vocab('invoice', inv.document_type)}
                            {inv.credits_invoice_label ? ` · ${t('invoices.credits', { label: inv.credits_invoice_label })}` : null}
                          </>
                        }
                      />
                      <span className="text-xs text-muted-foreground">
                        {t('invoices.issued')}: {formatDateTime(inv.issued_at)}
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1 text-sm">
                      <CustomerRef id={inv.customer_id} name={inv.customer_name} />
                      <span className="text-xs text-muted-foreground">
                        {t('invoices.total')}: <Money amount={inv.total} currency={inv.currency} />
                      </span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-muted-foreground">
                          <th className="py-1 pr-3 font-medium">{t('invoices.lines')}</th>
                          <th className="hidden py-1 pr-3 text-right font-medium sm:table-cell">{t('invoices.quantity')}</th>
                          <th className="hidden py-1 pr-3 text-right font-medium sm:table-cell">{t('invoices.unitPrice')}</th>
                          <th className="py-1 pr-3 text-right font-medium">{t('invoices.tax')}</th>
                          <th className="py-1 text-right font-medium">{t('invoices.amount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inv.lines.map((l) => (
                          <tr key={l.id} className="align-top">
                            <td className="py-1 pr-3">{l.description}</td>
                            <td className="tabular hidden py-1 pr-3 text-right sm:table-cell">{l.quantity}</td>
                            <td className="hidden py-1 pr-3 text-right sm:table-cell">
                              <Money amount={l.unit_price} currency={inv.currency} />
                            </td>
                            <td className="py-1 pr-3 text-right">
                              <Money amount={l.tax_amount} currency={inv.currency} />
                            </td>
                            <td className="py-1 text-right">
                              <Money amount={l.is_credit ? `-${l.amount}` : l.amount} currency={inv.currency} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      </Loaded>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Transfers, registrant changes, locks, expiry notices

export function TransferRows({ rows }: { rows: DomainTransfers['transfers'] }) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const columns: Column<DomainTransfers['transfers'][number]>[] = [
    {
      key: 'transfer',
      header: t('transfers.colDirection'),
      cell: (x) => <EntityRef label={vocab('direction', x.direction)} to={`/transfers/${encodeURIComponent(x.id)}`} id={x.id} sub={x.other_registrar} />,
    },
    { key: 'status', header: t('transfers.colStatus'), cell: (x) => <StatusBadge map={TRANSFER} value={x.status} /> },
    { key: 'requested', header: t('transfers.colRequested'), className: 'whitespace-nowrap', cell: (x) => formatDateTime(x.requested_at) },
    { key: 'by', header: t('transfers.requestedBy'), cell: (x) => <ActorName type={x.requested_by_type} name={x.requested_by_name} /> },
    {
      key: 'next',
      header: t('transfers.colNext'),
      cell: (x) => (
        <span className="text-xs">
          {x.completed_at
            ? t('transfers.completed', { when: formatRelative(x.completed_at) })
            : x.decided_at
              ? t('transfers.decided', { when: formatRelative(x.decided_at) })
              : x.auto_approve_at
                ? t('transfers.autoApprove', { when: formatRelative(x.auto_approve_at) })
                : '—'}
        </span>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(x) => x.id}
      empty={
        <EmptyState title={t('transfersTab.noTransfers')} icon={ArrowRightLeft}>
          {t('transfersTab.noTransfersBody')}
        </EmptyState>
      }
    />
  );
}

export function TransfersTab({ detail }: TabProps) {
  const { t } = useTranslation('domains');
  const tr = t as unknown as (key: string, options?: Record<string, unknown>) => string;
  const vocab = useVocab();
  const notice = useNoticeLabel();
  const d = detail.domain;
  const data = useApi((signal) => api.getDomainTransfers(d.id, { signal }), [d.id]);
  const changeColumns: Column<RegistrantChangeSummary>[] = [
    {
      key: 'change',
      header: t('changes.colTo'),
      cell: (x) => (
        <EntityRef
          label={x.to_display_name ?? <span className="italic">{t('common.unnamed')}</span>}
          to={`/registrant-changes/${encodeURIComponent(x.id)}`}
          id={x.id}
          sub={x.to_subject_type ? tr(`subject.types.${x.to_subject_type.toUpperCase()}`, { defaultValue: humanize(x.to_subject_type) }) : undefined}
        />
      ),
    },
    { key: 'from', header: t('changes.colFrom'), cell: (x) => <SubjectRef id={x.from_subject_id} name={x.from_display_name} type={x.from_subject_type} /> },
    { key: 'status', header: t('changes.colStatus'), cell: (x) => <StatusBadge map={TRANSFER} value={x.status} /> },
    { key: 'requested', header: t('changes.colRequested'), className: 'whitespace-nowrap', cell: (x) => formatDateTime(x.requested_at) },
  ];
  const lockColumns: Column<TransferLockRecord>[] = [
    {
      key: 'lock',
      header: t('transfersTab.lock'),
      cell: (l) => (
        <div className="flex flex-col items-start gap-1">
          <span className="font-medium">{vocab('lock', l.lock_type)}</span>
          {l.released_at ? (
            <Badge tone="neutral" icon={false}>
              {t('transfersTab.released')}
            </Badge>
          ) : (
            <Badge tone="warn">{t('transfersTab.active')}</Badge>
          )}
        </div>
      ),
    },
    { key: 'reason', header: t('transfersTab.reason'), cell: (l) => <SystemReason reason={l.reason} /> },
    { key: 'by', header: t('transfersTab.appliedBy'), cell: (l) => <ActorName type={l.applied_by_type} name={l.applied_by_name} /> },
    {
      key: 'applied',
      header: t('transfersTab.applied'),
      cell: (l) => (
        <div className="text-xs whitespace-nowrap">
          <div>{formatDateTime(l.applied_at)}</div>
          {l.released_at ? (
            <div className="text-muted-foreground">
              {t('transfersTab.released')}: {formatDateTime(l.released_at)}
            </div>
          ) : l.auto_release_at ? (
            <div className="text-muted-foreground">{t('transfersTab.autoRelease', { when: formatRelative(l.auto_release_at) })}</div>
          ) : null}
        </div>
      ),
    },
  ];
  const noticeColumns: Column<ExpiryNotice>[] = [
    { key: 'notice', header: t('transfersTab.notice'), cell: (n) => <span title={n.notice_type}>{notice(n.notice_type)}</span> },
    { key: 'expiry', header: t('transfersTab.expiry'), className: 'whitespace-nowrap', cell: (n) => formatDate(n.expiry_at) },
    { key: 'sent', header: t('transfersTab.sent'), className: 'whitespace-nowrap', cell: (n) => formatDateTime(n.created_at) },
  ];
  return (
    <Loaded state={data} failed={t('common.loadFailed')}>
      {(x) => {
        const active = x.locks.filter((l) => !l.released_at);
        return (
          <div className="grid grid-cols-1 gap-6">
            <Section title={t('transfersTab.state')} description={t('transfersTab.stateDescription')}>
              <KeyValue
                columns={3}
                rows={[
                  [t('transfersTab.transferState'), vocab('transferState', x.transfer_state)],
                  [t('transfersTab.authInfo'), x.auth_info_set_at ? formatDateTime(x.auth_info_set_at) : t('transfersTab.noAuthInfo')],
                  [
                    t('transfersTab.activeLocks'),
                    active.length ? (
                      <span key="l" className="flex flex-wrap gap-1">
                        {active.map((l) => (
                          <Badge key={l.id} tone="warn" title={l.lock_type}>
                            {vocab('lock', l.lock_type)}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      t('transfersTab.noActiveLocks')
                    ),
                  ],
                ]}
              />
            </Section>
            <Section flush title={t('transfersTab.transfers')}>
              <TransferRows rows={x.transfers} />
            </Section>
            <Section flush title={t('transfersTab.changes')}>
              <DataTable columns={changeColumns} rows={x.registrant_changes} rowKey={(c) => c.id} empty={<EmptyState title={t('transfersTab.noChanges')} icon={Users} />} />
            </Section>
            <Section flush title={t('transfersTab.locks')} description={t('transfersTab.locksDescription')}>
              <DataTable columns={lockColumns} rows={x.locks} rowKey={(l) => l.id} empty={<EmptyState title={t('transfersTab.noLocks')} icon={KeyRound} />} />
            </Section>
            <Section flush title={t('transfersTab.notices')}>
              <DataTable columns={noticeColumns} rows={x.expiry_notices} rowKey={(n) => n.id} empty={<EmptyState title={t('transfersTab.noNotices')} icon={Globe2} />} />
            </Section>
          </div>
        );
      }}
    </Loaded>
  );
}

